import React from 'react';
import { captureError } from '../utils/sentry';

// Detects the class of error thrown when webpack cannot fetch a lazy chunk
// (deploy replaced the hashed filename on the CDN, network blip, etc.).
function isChunkLoadError(err) {
  if (!err) return false;
  const name = err.name || '';
  const msg = String(err.message || err);
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

const RELOAD_FLAG = 'db_chunk_reload_at';

// Class component required — React error boundaries must be class-based.
export default class ChunkLoadErrorBoundary extends React.Component {
  state = { hasError: false, tooManyReloads: false };

  static getDerivedStateFromError(err) {
    if (isChunkLoadError(err)) {
      return { hasError: true, tooManyReloads: false };
    }
    // Not a chunk-load error → re-throw by not swallowing it.
    // Returning null means we do NOT render the fallback; React will bubble.
    throw err;
  }

  componentDidCatch(err) {
    if (!isChunkLoadError(err)) return;
    // Try to force a hard reload — the hashed filename is stale. Only ONE
    // reload per session; if we're still hitting the same error after a
    // reload, show a real UI instead of an infinite loop.
    let last = 0;
    try { last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0); } catch { /* ignore */ }
    const now = Date.now();
    // If we reloaded less than 15 s ago, don't loop.
    if (last && now - last < 15000) {
      captureError(err, { tags: { source: 'chunk-load', outcome: 'loop-detected' } });
      this.setState({ tooManyReloads: true });
      return;
    }
    try { sessionStorage.setItem(RELOAD_FLAG, String(now)); } catch { /* ignore */ }
    captureError(err, { tags: { source: 'chunk-load', outcome: 'reloading' } });
    // Delay slightly so the Sentry beacon leaves before navigation.
    setTimeout(() => { window.location.reload(); }, 100);
  }

  render() {
    if (this.state.tooManyReloads) {
      return (
        <div data-testid="chunk-load-error-fallback" style={{ padding: '32px', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
          <h2 style={{ marginBottom: '8px' }}>We couldn&apos;t load this page</h2>
          <p style={{ color: '#555', marginBottom: '16px' }}>A newer version of the app is available. Please refresh to continue.</p>
          <button
            data-testid="chunk-load-error-refresh-btn"
            onClick={() => { try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ } window.location.reload(); }}
            style={{ padding: '10px 20px', background: '#0F5D4E', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
          >
            Refresh now
          </button>
        </div>
      );
    }
    if (this.state.hasError) {
      // Reload is in flight — render a tiny placeholder so the user sees
      // something before the tab reloads.
      return <div data-testid="chunk-load-reloading" style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Updating…</div>;
    }
    return this.props.children;
  }
}
